import { getCurrentUser } from '../../profile/services';
import { apiCall } from '../../services/apiUtil';

/**
 * Squad Invitation Service
 *
 * Handles squad invitation accept/reject operations
 */
export const squadInvitationService = {
  /**
   * Accept a squad invitation
   * @param {number} groupId - ID of the group
   * @returns {Promise<Object>} - Result with success status
   */
  async acceptInvitation(groupId) {
    try {
      if (!groupId) {
        return { success: false, error: 'Group ID is required' };
      }

      // The backend identifies the accepting user from the JWT (see context.getUserInfo
      // in group_routes.js), not from the request body, so no client-side user lookup
      // is needed here. Resolving one via getCurrentUser() previously caused this call
      // to fail with "Not authenticated" on native (Android/iOS): getCurrentUser() reads
      // localStorage directly, but native sessions are persisted in secureStorage
      // (Capacitor Preferences), which is a separate store from the WebView's localStorage.
      const result = await apiCall(`/groups/${groupId}/accept-invitation`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      return { success: true, data: result };
    } catch (error) {
      console.error('🚨 Error accepting invitation:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Reject a squad invitation
   * @param {number} groupId - ID of the group
   * @param {number} userId - ID of the user rejecting (optional, defaults to current user)
   * @returns {Promise<Object>} - Result with success status
   */
  async rejectInvitation(groupId, userId = null) {
    try {
      const currentUser = getCurrentUser();
      const actualUserId = userId || currentUser?.id;

      if (!actualUserId) {
        return { success: false, error: 'Not authenticated' };
      }

      if (!groupId) {
        return { success: false, error: 'Group ID is required' };
      }


      await apiCall(`/groups/${groupId}/members/${actualUserId}`, { method: 'DELETE' });

      return { success: true };
    } catch (error) {
      console.error('🚨 Error rejecting invitation:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get invitation status for a user in a group
   * @param {number} groupId - ID of the group
   * @returns {Promise<Object>} - Result with status data
   */
  async getInvitationStatus(groupId) {
    try {
      const result = await apiCall(`/groups/${groupId}`);
      return {
        success: true,
        data: {
          status: result.data?.memberStatus ?? null,
          role: result.data?.userRole ?? null,
        },
      };
    } catch (error) {
      console.error('🚨 Error getting invitation status:', error);
      return { success: false, error: error.message };
    }
  },
};

export default squadInvitationService;
